import { RemoteMutatedFile } from '@k2/common/file-jet/file';
import { IdentifiableVendor, VendorIdentity } from '@k2/staff/content/clients/client/assignment/common/vendor-identity';
import { Field, Fields, FormSpec } from '../k2-forms-state/types';
import { BudgetValue } from '@k2/common/k2-forms/field-control/budget/budget-control.component';
import { ProductTransitQuote } from '@k2/staff/content/clients/client/assignment/hhg/costing/k2-costing/k2-costing-table/k2-costing-table.component';
import { Todo } from '@k2/common/ui/components/todo/todo.component';
import { Observable } from 'rxjs';
import { UntypedFormGroup } from '@angular/forms';
import { AddressesPayload, HhgAddress, HhgDestinationAddress, HhgOriginAddress, HhgOriginStorageAddress } from '@k2/staff/content/clients/client/assignment/hhg/overview/addresses/list/addresses-list.component';

export interface NumIndexed<T> {
  readonly [id: number]: T;
}

export interface LinkedAssignment {
  assignmentId: number;
  client: { id: number; name: string };
  assignee: { id: number; name: string };
}

export interface Assignment extends AssignmentRunningSummary {
  id: number;
  origin: Location;
  destination: Location;
  type: AssignmentType;
  type_id: number;
  package?: Package;
  status: RefType;
  possible_statuses: RefType[];
  person: Assignee;
  contact_user: ClientUser | number;
  relocation_manager: K2User;
  assignment_lead?: K2User;
  client?: Client;
  initiated_by: K2User & ClientUser;
  initiated_at: string;
  office: Office;
  billing: AssignmentBilling;
  notes: string;
  custom_fields: CustomField[];
  workflows: Workflow[];
  hhg_workflow: Workflow;
  services?: AssignmentService[];
  assignmentServicesInstances?: AssignmentServiceInstanceEnhanced[];
  import_document?: string | object;
  k2_invoicing_office: any;
  equus_service_order_id?: string;
  share_point_status?: number;
  hessel_list_item_id?: number;
  service_level_types_id: number | string;
  source?: string;
  assignee?: User;
  services_budget: ServiceBudget[];
  pop_up?: { message: string };
  is_assignment_encrypted: boolean;
  assignment_is_sent_to_workspace?: boolean;
  employee_is_invited_in_workspace?: boolean;
  closed?: boolean;
  employee_invited_on?: string;
  employee_invited_by?: string;
  employee_invitation_status?: string;
  is_sent_to_movetrack?: boolean;
  can_it_be_synced_with_movetrack?: boolean;
  sent_to_relocity?: {
    status: boolean,
    relocity_policy_id: number,
    relocity_client_company_id: number,
    update_button: boolean
  };
  hhgs?: HhgIdentity[];
  serviceInstances?: { [key: number]: ServiceInstanceMapItem };
}

export interface ServiceInstanceMapItem {
  name: string,
  label: string,
  serviceId: number,
  assignmentServiceInstanceId: number
}

export interface DspBundle {
  id: number;
  notes?: string;
}

export interface HhgIdentity {
  id: number;
  assignmentServiceInstance: HhgAssignmentServiceInstanceObj;
}

export interface HhgAssignmentServiceInstanceObj {
  id: number;
  parentId?: number;
  label: string;
  notes: string;
  notesAdditional: string;
  captiveServices: HhgCaptiveServices;
}

export interface ServiceBudget {
  id: number,
  budget: BudgetValue,
  label: string,
  responsibility: string
}

export interface AssignmentIdentity {
  readonly clientId: number;
  readonly assignmentId: number;
}

export interface CurrentK2UserAssignments {
  working_on: Assignment[];
  following: Assignment[];
  team: Assignment[];
  draft: Assignment[];
}

export interface CustomField<T = any> {
  id: number;
  value: T;
  config: {
    name: string;
    datatype: string;
    values: T[];
    required: 'YES' | 'NO';
  };
}

export interface Package {
  id: number;
  name: string;
  billed_as: string;
  billing_cycle: string;
  notes: string;
  status: 'ACTIVE';
  selected: boolean;
  active_services: string[];
  origin_countries: Country[];
  destination_countries: Country[];
  workflows: { [id: string]: Workflow };
  quotes: { [name: string]: PackageQuote };
}

export interface Workflow {
  workflow_id: number;
  name: string;
  priority: number;
  services: AssignmentService[];
}

export interface PackageQuote {
  lower: PackageQuotePart;
  upper: PackageQuotePart;
}

export interface PackageQuotePart {
  value: number;
  symbol: string;
  currency_iso3: string;
}

export interface Country {
  readonly id: number;
  readonly external_id?: string;
  readonly name: string;
  readonly currency_iso3: string;
}

export interface ActiveWorkflowServices {
  id: number;
  name: string;
  services: Array<{ id: number; name: string }>;
}

export interface HhgShipmentCaptiveServiceStorage {
  assignmentServiceInstance: HhgAssignmentServiceInstanceObj;
}

export interface HhgShipmentCaptiveServiceInsurance {
  assignmentServiceInstance: HhgAssignmentServiceInstanceObj;
}

export interface HhgCaptiveServices {
  storage?: HhgShipmentCaptiveServiceStorage;
  insurance?: HhgShipmentCaptiveServiceInsurance;
}

export interface AssignmentServiceInstanceCustomInformation {
  hhgId?: number;
  hasAnyVendorInitiated?: boolean;
  hasAnyChildren?: boolean;
}

export interface AssignmentService {
  id: number;
  type?: 'HHG' | 'SMD';
  name: string;
  assignmentServiceInstanceId: number;
  label: string;
  notes: string;
  slug: string;
  captiveServices: HhgCaptiveServices;
  variation_id: any;
  variation_label: string;
  variation_value: any;
  included_as: ServiceIncludedAs;
  billed_as: string;
  billing_cycle: string;
  origin_status: 'ORIGIN';
  price: any;
  service_definition: any[];
  budget: any;
  progress: AssignmentServiceProgress[];
  needed: boolean;
  workflow: RefType;
  can_be_bundled: boolean;
  addresses?: AddressesPayload;
}

export type ServiceIncludedAs = 'CORE' | 'FLEXIBLE' | 'DISABLED';

export interface AssignmentServiceProgress {
  value: AssignmentServiceStatus;
  label: string;
}

export type AssignmentServiceStatus = 'Not started' | 'In progress' | 'Completed';

export interface AssignmentBilling {
  finance_id: number;
  address: Address;
  currency: Currency;
  subsidiary: Subsidiary;
}

export interface Address {
  id: number;
  name: string;
  salutation: string;
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  city: string;
  postal_code: string;
  county: string;
  country_name: string;
  countries_id: number;
  country: string;
  latitude: string;
  longitude: string;
  status: 'ACTIVE';
}

export function toAddress(value: HhgAddress | HhgOriginAddress | HhgOriginStorageAddress | HhgDestinationAddress): Address {
  // check if it is HhgOriginAddress, HhgOriginStorageAddress, HhgDestinationAddress
  if ('address' in value) {
    value = value.address;
  }

  return {
    id: value.id,
    name: value.label,
    salutation: undefined,
    address1: value.line1,
    address2: value.line2,
    address3: value.line3,
    address4: value.line4,
    city: value.city,
    postal_code: value.postalCode,
    county: value.county,
    country_name: value.country?.name,
    countries_id: value.country?.id,
    country: value.country?.iso2,
    latitude: undefined,
    longitude: undefined,
    status: 'ACTIVE'
  };
}

export interface Subsidiary {
  id: number;
  name: string;
  short_name: string;
  logo: RemoteMutatedFile;
}

export interface Assignee extends ClientUser {
  party: AssigneeParty;
  assignments: AssignmentRunningSummary[];
  has_any_claim: boolean;

}

export interface AssignmentRunningSummary {
  is_running: boolean;
  start_date: string;
  end_date?: string;
}

export interface AssigneeParty {
  partner: AssigneePartyMember;
  children: AssigneePartyMember[];
  other: AssigneePartyMember[];
  pets: Pet[];
}

export interface AssigneePartyMember {
  id: string;
  uniqId: string;
  first_name: string;
  last_name: string;
  gender: Gender;
  dob: string;
  nationality: string;
  marital_status: string;
  telephone_home: string;
  telephone_work: string;
  telephone_mobile: string;
  notes: string;
}

export interface Pet {
  id: string;
  uniqId: string;
  name: string;
  species: string;
  breed: string;
  age: number;
  weight: number;
  notes: string;
}

export interface Delegate {
  id: string;
  uniqId: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
}

export type Gender = 'male' | 'female' | 'unknown';

export type AssignmentType =
  | 'Long term assignment (LTA)'
  | 'Short term assignment (STA)'
  | 'Permanent transfer (PT)'
  | 'Commuter'
  | 'New hire'
  | 'Rotational';

export interface RefType<TLabel = string, TName = string, TValue = string> {
  id: number;
  type_label: TLabel;
  type_name: TName;
  type_value: TValue;
}

export interface Location {
  id: number;
  name: string;
  city_ascii: string;
  city_utf8: string;
  country: string;
  country_iso: string;
  country_iso2: string;
  latitude: string;
  longitude: string;
  timezone: string;
  region: string;
}

export interface Region {
  id: number;
  name: string;
  country_iso: string;
}

export interface Office {
  id: number;
  name: string;
  location: Location;
  team: K2User[];
}

export interface Client {
  id: number;
  name: string;
  short_name: string;
  logo: RemoteMutatedFile;
  status: ClientStatus;
  office: Office;
  region: Region;
  notes: string;
  account_director: K2User;
  client_contact: K2User;
  assignment_stats: ClientAssignmentStats;
  needs_analysis_required: boolean;
  sent_to_relocity: Relocity;
  skip_budget: boolean;
  documents_section_visible: boolean;
}

export interface Relocity {
  status: boolean;
  destination_city_id: number;
  relocity_policy_id: number;
}

export type ClientStatus = 'ACTIVE' | 'DELETED' | 'DEMO';

export interface ClientAssignmentStats {
  total: number;
  current_year: number;
  current: number;
}

export interface ClientStructure {
  entity: Partial<Client>;
  children: SubsidiaryStructure[];
}

export interface SubsidiaryStructure {
  entity: Partial<Subsidiary>;
  children: SubsidiaryStructure[];
}

export interface NormalizedClientStructure {
  entity: number;
  children: NormalizedClientStructure[];
}

export interface PartnerContact extends User {
  responsibility: string;
  main_contact: boolean;
}

export interface ClientUser extends User {
  subsidiary: Subsidiary;
  is_admin: boolean;
  responsibility: string;
}

export interface K2User extends User {
  leaving_at: string;
  reports_to: K2User;
  assignment_stats: K2UserAssignmentStats;
  is_dismissed: boolean;
}

export interface K2UserAssignmentStats {
  active: number;
  ongoing: number;
}

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  avatar: RemoteMutatedFile;
  email: string;
  telephone: string;
  telephone_work: string;
  telephone_extension: string;
  telephone_mobile: string;
  telephone_home: string;
  job_title: string;
  office: Office;
  last_seen_at: string;
  ustatus: UserStatus;
  role: string;
  role_name: string;
  roles_id: number;
  assignments_id?: number;
}

export type UserStatus = 'FIRSTLOGIN' | 'NORMAL' | 'SUSPENDED' | 'DELETED';

export interface ServiceException {
  id: number;
  cost_to: 'CLIENT' | 'K2';
  // created_at: string;
  approver: K2User | number;
  // exception_type: string;
  requested_at: string;
  // reason?: string;
  service: Service | number;
  cost: Cost;
  assignment: Assignment | number;
  days?: { value: any; label: string };
  status: ServiceExceptionStatus;
}

export interface ServiceExceptionStatus {
  label: ServiceExceptionStatusEnum;
  changed_by: { first_name: string; last_name: string };
  changed_at: string;
}

export type ServiceExceptionStatusEnum = 'PENDING' | 'ACCEPTED' | 'REJECTED';

export interface ServiceExceptionIdentity {
  id: number;
  client: number;
  assignment: number;
  service: number;
}

export interface Cost {
  billing_cycle: string;
  amount: number;
  currency: Currency;
}

export interface Currency {
  id: number;
  name: string;
  symbol: string; // todo: not necessary as Angular will handle it
  iso3: string;
}

export interface Service {
  id: number;
  name: string;
  slug: string;
  status: string;
  full_name: string;
  enabled: boolean;
  variations: ServiceVariation[];
  provided_at: string;
  num_regions: number;
  workflow: Workflow;
  canBeBundled: boolean;
}

export interface ServiceHhgShipment {
  id: number;
}

export interface AssignmentServiceInstance {
  id: number;
  label: string;
  service: Service;
  serviceHhgShipment: ServiceHhgShipment;
}

export interface AssignmentServiceInstanceEnhanced {
  id: number;
  parentId?: number;
  service: AssignmentService;
  label: string;
  notes?: string;
  isNeeded: boolean;
  dspBundle?: DspBundle;
  customInformation?: AssignmentServiceInstanceCustomInformation;
}

export interface Workflow {
  type_label: string;
}

export interface Product {
  product_name: string;
  product_id: number;
  product_groups_id: number;
  status: string;
  commercial_adjustment_exempt: boolean;
  mark_up_exempt: boolean;
}

export interface ServiceVariation {
  id: number;
  label: string;
  description: string;
  value: number | string;
  enabled: boolean;
  locked: boolean;
}

export interface AssignmentStatusReport {
  status: string;
  count: number;
}

export interface MovementReport {
  aboutToStart: Movement[];
  aboutToEnd: Movement[];
}

export interface Movement {
  origin: City;
  destination: City;
}

export interface City {
  id: number;
  external_id?: string;
  name: string;
  region: string;
  country: string;
  city_utf8: string;
  city_ascii: string;
  latitude: string;
  longitude: string;
  timezone: string;
}

export interface Document extends DocumentFile {
  status: string;
  created_at: string;
  document_type: string;
  category_label: string;
  file_types_id: number;
  viewing_permissions?: string[];
  selected_asinst_labels: string[];
  uploader: K2User;
  category_definition: { photo_type: boolean };
  photo_categories: string[];
}

export interface DocumentFile {
  id: number;
  identifier_hash: string;
  name: string;
  mime_type: string;
  download_filename?: string;
}

export interface ExceptionFile {
  id: number;
  identifier_hash: string;
  name: string;
  mime_type: string;
}

export interface Exception extends ExceptionFile {
  summary: string;
  exceptionFor: string;
  item_amount: number;
  cost: { currency: Currency, amount: number };
  status: { label: string };
  decision_date: string;
  client_contact: string;
  created_at: string;
  suggested_resolution: string;
  requested_at: string;
  category_label: string;
}

export interface CostSaving {
  id: number;
  amount: number;
  comments: string;
  created_at: string;
  currencies_id: number[];
  detail: string;
  products_id: number[];
  requested_at: string;
}

export interface PopulationReport {
  destination: { [name: string]: number };
  policy: { [name: string]: number };
  type: { [name: string]: number };
  genders: { [name: string]: number };
  duration: { [name: number]: number };
  destination_city_geolocations: Array<{
    name: string;
    latitude: string;
    longitude: string;
    count: number;
  }>;
  top_origin_locations: Array<{
    country_name: string;
    iso: string;
    location_name: string;
    count: number;
  }>;
  top_destination_locations: Array<{
    country_name: string;
    iso: string;
    location_name: string;
    count: number;
  }>;
  total_assignments: number;
  lanes: Array<{ count: string; regions: Array<{ id: string; name: string }> }>;
}

export interface FeedbackReport {
  client_feedback: ClientFeedback;
}

export interface ClientFeedback {
  all_time: FeedbackTimeframeReport;
  this_year: FeedbackTimeframeReport;
  this_quarter: FeedbackTimeframeReport;
}

export interface FeedbackTimeframeReport {
  readonly totalRequested: number;
  readonly totalRecieved: number;
  readonly totalAwaiting: number;
  subsidiaries: FeedbackSubsidiary[];
  readonly overall_satisfaction_amount: number;
  readonly overall_satisfaction_rating: number;
  readonly overall_satisfaction_percentage: number;
  readonly would_recommend_amount: number;
  readonly would_recommend_rating: number;
  readonly would_recommend_percentage: number;
  comments: FeedbackComment[];
}

export interface FeedbackSubsidiary {
  name: string;
  totalRequested: number;
  totalRecieved: number;
  totalAwaiting: number;
}

export interface FeedbackComment {
  date: { date: string; timezone_type: number; timezone: string };
  rating: number;
  assignment: Assignment;
  comment: string;
}

export interface YearOnYearReport {
  perMonth: Array<{ count: number; month: number; year: number }>;
  perOrigin: Array<{ count: number; origin: string; year: number }>;
  perDestination: Array<{ count: number; origin: string; year: number }>;
  bySubsidiary: Array<{ count: number; subsidiary: string; year: number }>;
  services: Array<{ name: string; year: number; count: number }>;
}

export interface StorageReport {
  storage_usage: StorageUsage;
  vendors: Array<{
    assignment: Assignment;
    vendor: Partner;
    location: Location;
    terminal: string;
    totalVolume: number;
    totalContainers: number;
  }>;
}

export interface StorageUsage {
  readonly assignments: number;
  readonly containers: number;
  readonly volume: number;
}

export interface StorageUsageChartData {
  [year: string]: { [month: string]: number };
}

export interface DemographicReport {
  age: AgeDistribution[];
  maritalStatus: MaritalStatusDistribution[];
  children: ChildrenDistribution[];
}

export interface AgeDistribution {
  age: number;
  male: number;
  female: number;
  unknown: number;
}

export interface MaritalStatusDistribution {
  status: string;
  count: string;
}

export interface ChildrenDistribution {
  children_count: number;
  count: number;
}

export interface MonthlyInitiations {
  readonly month: number;
  readonly count: number;
}

export interface YearlyInitiations {
  readonly year: number;
  readonly monthly: MonthlyInitiations[];
}

export interface TopClientReport {
  readonly id: string;
  readonly name: string;
  readonly count: string;
}

export interface TopDestinationReport {
  readonly city_id: string;
  readonly country: string;
  readonly city: string;
  readonly count: string;
}

export interface TopPartnerReport {
  readonly id: string;
  readonly name: string;
  readonly avg_rating?: string;
  readonly count: string;
}

export interface TopServiceReport {
  readonly service_id: string;
  readonly service_name: string;
  readonly count: string;
}

export interface UserSessionsHistory {
  current: UserSession[];
  history: UserSession[];
}

export interface UserSession {
  browser: string;
  country: string;
  country_name: string;
  id: string;
  ip: string;
  os: string;
  time: string;
}

export interface Task {
  id: number;
  name: string;
  alertable: boolean;
  alert: boolean;
  description: string;
  status: string;
  start_date: string;
  assignmentServiceInstance: AssignmentServiceInstance;
  assignment: Assignment;
  assigned_to: K2User;
  client: Client;
  is_auto: boolean;
}

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface UpdateActivity {
  created_at: string;
  id: number;
  first_name: string;
  last_name: string;
}

export interface PartnerService {
  id: number;
  name: string;
}

export interface Partner {
  hhg_services: PartnerService[];
  id: number;
  name: string;
  type: string;
  switchboard: string;
  email: string;
  website: string;
  avatar: RemoteMutatedFile;
  regions: number[];
  regions_states: number[];
  services: PartnerService[];
  is_parent: boolean;
  partner?: Partner;
  k2_rating: number;
  currency_iso3: string;
  currency_name: string;
  about: string;
  notes: string;
  parent?: Partner;
  status: string;
  k2_manager: K2User;
  sage_status: string;
  k2_office: Office;
  subtype: string;
  avatar_workspace?: string;
}

export interface PartnerChild {
  id: number;
  avatar: RemoteMutatedFile;
  currency_name: string;
  name: string;
  sage_status: string;
  status: string;
  subtype: string;
  k2_manager: K2User;
  managing_office: Office;
}

export interface PartnerInitiation {
  initiated_at: string;
  assignment: Assignment;
  client: Client;
  service: Service;
}

export interface PartnerFeedback {
  average_rating: number;
  rating_breakdown: {
    1: number;
    2: number;
    3: number;
    4: number;
  };
  recommend_percent: number;
  history: {
    rows: PartnerFeedbackHistoryRecord[];
    total_results: number;
  };
}

export interface PartnerFeedbackHistoryRecord {
  average_rating: number;
  completed_at: string;
  assignments_id: number;
  clients_id: number;
  notes: string;
  responses: Array<{
    question: string;
    response: string;
  }>;
}

export interface OfficeInitiationStats {
  office: Office;
  one_day: number;
  seven_days: number;
  thirty_days: number;
}

export type RegionType = 'origin' | 'destination';

export interface FeedbackQuestions {
  [type: string]: FeedbackQuestion[];
}

export interface FeedbackQuestion {
  id: number;
  priority: number;
  question: string;
}

export type TransitItemType = 'crated' | 'bulky' | 'dismantle';

export interface TransitMethodTypeDefinition {
  priority: number;
  linked_service_id: number;
}

export interface TransitMethod extends RefType {
  type_class: string;
  type_definition: TransitMethodTypeDefinition;
}

export interface PackingMaterialMeasurement {
  type: 'volume' | 'width' | 'length' | 'depth' | 'weigth';
  unit: 'ft' | 'in' | 'ft3' | 'mm' | 'cm' | 'm' | 'm3' | 'g';
  value: number;
}

export interface PackingMaterialItem {
  name: string;
  type: 'furniture' | 'box' | 'crate' | 'paper' | 'plastic';
  baseType: 'default' | 'other';
  quantity: number;
  measurements: PackingMaterialMeasurement[];
}

export interface PackingMaterials {
  measurementSystem: 'imperial' | 'metric';
  items: PackingMaterialItem[];
}

export interface TransitEntity {
  id: string;
  transit_name: string;
  destination_name: string;
  transit_method: TransitMethod;
  requested_pack_dates: string;
  requested_delivery_dates: string;
  allowance_in_place: boolean;
  transit_move_allowance?: number;
  transit_move_allowance_measurement_unit?: string;
  monetary_allowance?: number;
  monetary_currency?: string;
  insurance_rate?: number;
  insurance_specifics: string;
  packing_partner?: Partner;
  packing_materials: PackingMaterials | [];
  required_road_method: string;
}

export interface UserSecurity {
  auth2fa_enabled: boolean;
  status_2fa: string;
  auth2fa_qr_url?: string;
  secret_response?: string;
  auth2fa_status?: string;
  auth2fa_recovery_codes: string[];
}

export interface EmailSummary {
  id: number;
  date: string;
  subject: string;
  from_address: string;
}

export interface Email {
  id: number;
  date: string;
  subject: string;
  sender: string;
  recipients: string[];
  body_plain: string;
  body_html: string;
  attachments: Attachment[];
}

export interface Attachment {
  id: number;
  file_name: string;
}

export interface Vendor extends IdentifiableVendor {
  name: String;
  details: FormSpec;
  initiated_at: string;
  service_dates: DatesInterval | any[];
  contact: any;
  call_date: string;
  vendor_will_contact: boolean;
  expected_cost: any;

  // set on UI for confirmations
  isSpecialistService?: boolean;
}

export interface DatesInterval {
  start_date: string;
  end_date: string;
}

export class SubsectionIdentity {
  constructor(readonly vendor: VendorIdentity, readonly subsectionId: string) {
  }
}

export class SubsectionItemIdentity {
  constructor(readonly subsection: SubsectionIdentity, readonly itemId: string) {
  }
}

/** HHG Transit **/

export interface PartnerTransitQuotes {
  transit_id: string,
  transit_label: string,
  quotes: TransitQuote[],
  insurances: Insurance[],
  totals: { totals: Total }
}

export interface Insurance {
  id: number,
  description: string,
  insurance_type: string,
  quote: { amount: number, iso3: string }
}

export interface Total {
  boxes: {
    volume: { ft: number, m: number }
    weight: { lb: number, kg: number }
  },
  bulkyItems: {
    volume: { ft: number, m: number }
    weight: { lb: number, kg: number }
  },
  cratedItems: {
    volume: { ft: number, m: number }
    weight: { lb: number, kg: number }
  },
  furniture: {
    volume: { ft: number, m: number }
    weight: { lb: number, kg: number }
  },
  total: {
    volume: { ft: number, m: number }
    weight: { lb: number, kg: number }
  }
}

export interface TransitQuote {
  label: string,
  product_rows: ProductRow[],
  quote_headers: QuotePartner[],
  vendor_quotes: VendorQuote,
  final_costs: TransitQuoteFinalCosts
}

export interface SpecialistService {
  transit_id: string,
  transit_label: string,
  product_rows: ProductRow[],
  quote_headers: QuotePartner[],
  vendor_quotes: VendorQuote,
  final_costs: TransitQuoteFinalCosts
}

interface TransitQuoteFinalCosts {
  transit_id: string,
  vendor_transit_quotes: { [key: string]: TransitQuoteCost },
  subtotal: { value: number, currency: Currency, converted_value: number }[]
}

interface TransitQuoteCost {
  product_key: string,
  product_name: string,
  product_description: string,
  quote: {
    vendor: QuotePartner,
    quote_id: string,
    quote_amount: number,
    quote_currency: Currency,
    quote_converted_amount: number,
    quote_converted_currency: Currency
  }
}

interface ProductRow {
  id: number,
  name: string,
  product_key: string
}

interface QuotePartner {
  id: number,
  name: string,
  quote_id?: number
}

interface VendorQuote {
  [key: string]: VendorQuoteItem;
}

interface VendorQuoteItem {
  product_key: string,
  product_name: string,
  quotes: Quote[],
  // for UI
  partnerQuotes: { [id: number]: { currency: Currency, amount: number, quote_amount: number } }
  partnerOptions: QuotePartner[];
  selectedVendor: QuotePartner
}

interface Quote {
  commercial_adjustment: number,
  commercial_adjustment_enabled: boolean,
  currency_id: number,
  description: string,
  entered_currency: Currency,
  product_key: string,
  quote_amount: number,
  quote_amount_without_conversion: number,
  vendors_id: number,
  service_status_selected: boolean
}

export interface PartnerTeam {
  commercial_manager: K2User;
  service_review: K2User;
  new_service_initiation: K2User;
  service_escalation: K2User;
  rates_responsible: K2User;
  managing_office: number;
}

export interface Stat {
  value: number;
  name: string;
}

export interface PartnerStats {
  total_relo: Stat;
  top3: Stat[];
}

export interface PartnerBrief {
  section1?: string;
  section2?: string;
  section3?: string;
  section4?: string;
  section5?: string;
  section6?: string;
  section7?: string;
}

export interface PartnerDetails {
  name: string;
  city: string;
  county: string;
  countries_id: number;
  currencies_id: number;
  partner_level_list_id: any[];
  partner_type_ref_types_id: number;
  is_parent: boolean;
  is_prohibited: boolean;
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  postal_code: string;
  managing_office: number;
  partner_subtype_id: number;
}

export interface PartnerReview {
  by: number;
  date: string;
  next_date: string;
  notes: string;
  partner_contact: number;
  summary: string;
  link_documents: number[][];
}

export interface PartnerRegions {
  regions: number[];
  states: number[];
}

export interface PartnerAccreditation {
  comments: string;
  name: string;
  rating: string;
  release_date: string;
  status: string;
}

export interface PartnerSustainability {
  plastic_free: boolean;
}

export interface PartnerPrice {
  id: number;
  currency_iso3: string;
  currency_symbol: string;
  details: string;
  name: string;
  price: string;
  vendors_id: number;
  service_internal_ref_types_id: number;
}

export interface PartnerDocument {
  id: number;
  name: string;
  file_identifier: string;
  mime_type: string;
  file_types_id: string;
  created_at: string;
  status: string;
  document_type: string;
  identifier_hash: string;
  legacy_identifier: string;
  migrated: string;
  reference: string;
  category_label: string;
  viewing_permissions: string[];
  photo_categories: string[];
  uploader: K2User;
}

export interface K2CostingOptionsData {
  transits: {
    [transitId: string]: {
      [quoteId: number]: { [productKey: string]: { markup: number, currency_adjustment_factor: number } }
    }
  },
  specialist_services: {
    [quoteId: number]: { [productKey: string]: { markup: number, currency_adjustment_factor: number } }
  }
}

export interface ClientSettings {
  readonly insurance_selling_rate: number;
  readonly cubic_meters_value: string;
  readonly quoting_commitment: string;
  readonly quoting_currency: Currency;
  readonly client_mark_up: number;
  readonly is_recognised_currency: boolean;
}

export interface MoveAdditionalDetails {
  move_allowance: string;
  insurance_rate: string,
  insurance_specifics: string,
  requested_pack_dates: string,
  requested_delivery_dates: string,
  transit_move_allowance_measurement_unit: string,
  required_road_method: string,
  form: FormSpec
}

export interface MoveDetails {
  gross?: string,
  gross_measurement_unit?: string,
  acw?: string,
  acw_measurement_unit?: string,
  density?: string,
  apod?: string,
  apoe?: string,
  advise_net_gross?: string,
  dthc_lcl?: string,
  dthc_fcl?: string,
  pod_lcl?: string,
  pod_fcl?: string,
  poa_lcl?: string,
  poa_fcl?: string,
  sachets_air?: string,
  sachets_lcl?: string,
  sachets_fcl?: string,
  days_for_pack_lcl?: string,
  days_for_pack_fcl?: string,
  days_for_pack_road?: string,
  rate_based_on?: string,
  part_load_estimated_time?: string,
  planned_timeline?: string,
  packing_type?: string,
  estimated_time_air?: string,
  estimated_time_lcl?: string,
  estimated_time_fcl?: string,
  need_to_be_cased?: string,
  advise_total_volume?: string,
  advise_net_volume?: string,
  advise_gross_volume?: string,
  requested_delivery_dates?: string,
  requested_pack_dates?: string,
  advise_gross_volume_measurement_unit?: string,
  advise_total_volume_measurement_unit?: string,
}

export interface Transit {
  transit_id: string,
  transit_label: string,
  transit_method: string,
  move_additional_details: MoveAdditionalDetails,
  insurances: Insurance[],
  totals: { totals: Total },
  quotes: FinalCostQuote[]
}

export interface ReviewTransit {
  transit_id: string,
  transit_label: string,
  transit_method: string,
  move_details: MoveDetails,
  destination_name: string,
  id: string,
  transit_name: string,
  in_bundle: boolean,
  door_to_door: { quote_id: string, quote_amount: number | string }[],
  quote_headers: {
    id: string,
    name: string,
    quote_id: string,
    quote_label: string
  }[],
  product_rows: {
    product_id: number,
    product_key: string,
    product_label: string,
    product_name: string,
  }[]
}

export interface Costing {
  client_config: ClientSettings,
  quote_options_costing: {
    client_settings: {
      quoting_currency: Currency
    }
    rounding: Field,
    final_costs: {
      specialist_services: { [productKey: string]: ProductTransitQuote },
      transits: Transit[]
    }
  },
  todos: Todo[]
}

export interface FinalCostQuote {
  quote_id: string,
  vendor_transit_quotes: PartnerTransitQuotes,
  subtotal: {
    quote_amount: { value: string, currency: Currency }[],
    negotiated_amount?: { value: string, currency: Currency }[],
    quote_converted_amount: string,
    final_amount: string,
    final_currency: Currency,
  },
  subtotal_insurance: {
    final_amount: string,
    final_currency: Currency
  },
  insurance: {
    quote_converted_amount: number,
    final_amount: number
  },
  commercial: {
    markup: string,
    margin_exc: string,
    margin_inc: string
  }
}

export interface PropertyDetails {
  readonly addressId: number | string;
  readonly label: string;
  readonly fields: Observable<Fields>;
  readonly address: any;
  readonly form: Observable<UntypedFormGroup>;
}
